import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
  DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleFilled,
  SearchOutlined,
  FilterOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { api, http } from '../api/client';
import { useBatches, batchLabel } from '../hooks/useBatches';
import { RecognitionSummarySection } from '../components/RecognitionSummarySection';

/**
 * 「职级晋升人员」管理员页面（V1.4 新增）。
 *
 * 流程：
 *   1. 选批次、调权重、调比例（默认 25%） → 拉「候选名单」
 *   2. 候选名单展示在表格里，admin 可改 4 列（拟晋升职级/薪资/理由），可删除、可从「候选池」加人
 *   3. 「确认入库」→ 写 PromotionRecord，「导出 Excel」→ 下载 .xlsx
 *
 * 设计取舍：
 *   - 草稿仅留在前端 state（不另起 draft 表）；admin 可以反复"重新生成"切回系统建议
 *   - 入库走覆盖式：只清理本批次 semester + 当前 items 中的 employeeId，不动其它人手维护的同期记录
 */

type CandidateRow = {
  employeeId: number;
  empNo: string;
  deptPath: string | null;
  name: string;
  position: string;
  jobGrade: string | null;
  jobLevel: number | null;
  /** 当前职级月薪基数（含 1810/餐补/忠诚奖励金）。HR 维护到 Employee 表，可空。 */
  currentLevelSalary: number | null;
  /** 过往 6 个月实发到手平均（元）。HR 每期前刷新到 Employee 表，可空。 */
  avgPayLast6Months: number | null;
  historyPromotion: string;
  proposedLevelText: string;
  proposedAmount: number | null;
  proposedReason: string;
  scores: {
    ninebox: { value: number; finalCell: string | null };
    nomination: {
      value: number;
      nominators: string[];
      targetRanks: string[];
    };
    competency: {
      value: number;
      weighted: number | null;
      redLineHit: boolean;
    };
    hp: { value: number; label: string | null };
    composite: number;
  };
};

type CandidatesResp = {
  batch: {
    id: number;
    name: string;
    displayName: string | null;
    defaultConfirmYearMonth: string;
    performancePeriodMonths: string[];
  };
  weights: {
    ninebox: number;
    nomination: number;
    competency: number;
    hp: number;
  };
  percent: number;
  totalActive: number;
  excludedRedLine: number;
  nominatedTotal: number;
  candidates: CandidateRow[];
  pool: CandidateRow[];
};

type SavedResp = {
  defaultConfirmYearMonth: string;
  performancePeriodMonths: string[];
  records: Array<{
    id: number;
    employeeId: number;
    employee: {
      id: number;
      name: string;
      empNo: string;
      position: string | null;
      deptPath: string | null;
      jobGrade: string | null;
      jobLevel: number | null;
    };
    yearMonth: string;
    levelText: string;
    amount: number | null;
    note: string | null;
    updatedAt: string;
  }>;
};

const DEFAULT_WEIGHTS = {
  ninebox: 30,
  nomination: 30,
  competency: 25,
  hp: 15,
};

// 自訂欄（admin 可在任意位置插入）
type CustomColumn = { id: string; title: string };
type ColumnOrderItem =
  | { kind: 'base'; key: string }
  | { kind: 'custom'; id: string };

// 基礎欄位的固定 key 順序；任何插入/刪除自訂欄都圍繞這份順序展開
const BASE_COLUMN_KEYS = [
  'index',
  'recognitionType',
  'deptPath',
  'name',
  'position',
  'jobGrade',
  'jobLevel',
  'currentLevelSalary',
  'avgPayLast6Months',
  'composite',
  'historyPromotion',
  'proposedLevelText',
  'proposedAmount',
  'proposedReason',
  'confirmStatus',
  'actions',
] as const;

const DEFAULT_COLUMN_ORDER: ColumnOrderItem[] = BASE_COLUMN_KEYS.map((key) => ({
  kind: 'base',
  key,
}));

// 用于「在「xxx」左侧/右侧插入列」提示语
const BASE_COL_LABEL: Record<string, string> = {
  index: '#',
  recognitionType: '晋升',
  deptPath: '部门',
  name: '姓名',
  position: '职位',
  jobGrade: '职级',
  jobLevel: '等级',
  currentLevelSalary: '目前职级薪资',
  avgPayLast6Months: '过往6个月到手工资',
  composite: '综合分',
  historyPromotion: '历史晋升',
  proposedLevelText: '本次拟晋升职级',
  proposedAmount: '涨薪（元）',
  proposedReason: '晋升理由',
  confirmStatus: '晋升状态',
  actions: '操作',
};

// 晋升 三选项 · 多选。
// 业务语义：
//   - 直接晋升：常规节奏，符合职级路径与综合分门槛
//   - 挑战晋升：跨级晋升或非常规节奏；通常带挑战目标 / 重大业绩
//   - 不晋升：本次明确不晋升（与"未选"区分；选了"不晋升"会自动排到表底部）
// 多选规则：保留 mode='multiple' 不破坏 localStorage 数据结构（仍是数组）。
//
// 2026-05-12 改造（农俊董事长要求）：列名「表彰/晋升」→「晋升」；
//   下拉选项从「直接晋升 / 挑战晋升 / 表彰」改为「直接晋升 / 挑战晋升 / 不晋升」。
// 兼容存量数据：保留 'praise' 类型，但只作为历史兼容值（不出现在下拉框）；
//   - localStorage 中已存的 'praise' 不被擦除（RECOGNITION_VALUE_SET 仍认它合法）
//   - 已选 'praise' 的行：行染色继续按"橙黄"渲染；下拉里以"表彰（旧）"灰显占位，禁用不可再选
//   - 新创建/新勾选的行只能从「直接晋升 / 挑战晋升 / 不晋升」里选
type RecognitionType =
  | 'direct_promote'
  | 'challenge_promote'
  | 'not_promote'
  | 'praise'; // legacy，保留以兼容历史 localStorage

type RecognitionOption = {
  value: RecognitionType;
  label: string;
  rowClass: string;
  selectColor: string;
};

// 当前活跃下拉选项（用户可见、可选）
const RECOGNITION_OPTIONS: Array<RecognitionOption> = [
  {
    value: 'direct_promote',
    label: '直接晋升',
    rowClass: 'tg-row-direct-promote',
    selectColor: '#52c41a',
  },
  {
    value: 'challenge_promote',
    label: '挑战晋升',
    rowClass: 'tg-row-challenge-promote',
    selectColor: '#1677ff',
  },
  {
    value: 'not_promote',
    label: '不晋升',
    rowClass: 'tg-row-not-promote',
    selectColor: '#8c8c8c',
  },
];

// 仅用于历史兼容（不在下拉显示，不允许新选；染色 / 标签查找仍走这里）
const RECOGNITION_LEGACY_OPTIONS: Array<RecognitionOption> = [
  {
    value: 'praise',
    label: '表彰',
    rowClass: 'tg-row-praise',
    selectColor: '#fa8c16',
  },
];

// 合并表：行染色 / 标签查找用这个，确保旧值 'praise' 仍能识别
const ALL_RECOGNITION_OPTIONS: Array<RecognitionOption> = [
  ...RECOGNITION_OPTIONS,
  ...RECOGNITION_LEGACY_OPTIONS,
];

// RECOGNITION_VALUE_SET 必须包含全部历史值，否则 load localStorage 时会把 'praise' 过滤掉，
// 等同于自动擦除老板已经选好的数据——违反「原选项不要动」铁律。
const RECOGNITION_VALUE_SET = new Set<RecognitionType>(
  ALL_RECOGNITION_OPTIONS.map((o) => o.value),
);

export function PromotionPlanPage() {
  const { batches, batchId, setBatchId } = useBatches();

  const recognitionBatchExportTag = useMemo(() => {
    if (!batchId) return undefined;
    const b = batches.find((x) => x.id === batchId);
    return b ? batchLabel(b) : String(batchId);
  }, [batchId, batches]);

  const [percent, setPercent] = useState<number>(25);
  const [weights, setWeights] = useState({ ...DEFAULT_WEIGHTS });
  // 用 lastFetchedKey 触发重拉；切批次时也清掉本地 rows
  const [generationKey, setGenerationKey] = useState(0);
  const [rows, setRows] = useState<CandidateRow[]>([]);
  const [pool, setPool] = useState<CandidateRow[]>([]);
  const [meta, setMeta] = useState<{
    totalActive: number;
    excludedRedLine: number;
    nominatedTotal: number;
    defaultConfirmYearMonth: string;
    performancePeriodMonths: string[];
  } | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmYearMonth, setConfirmYearMonth] = useState('');
  const [pendingCommit, setPendingCommit] = useState<CandidateRow[]>([]);
  const [poolDrawerOpen, setPoolDrawerOpen] = useState(false);
  const [poolSearch, setPoolSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // 「晋升候选 → 确定晋升」逐行 toggle；只有 confirmed=true 的行才能入库
  const [confirmedSet, setConfirmedSet] = useState<Set<number>>(new Set());
  // 顶部筛选（覆盖姓名/部门/职位/工号/职级 — 任何文本字段子串匹配）
  type PromotionTableFilter = RecognitionType | 'all' | 'unset';
  const [fPromotion, setFPromotion] = useState<PromotionTableFilter>('all');
  const [fDept, setFDept] = useState<string | undefined>();
  const [fName, setFName] = useState<string | undefined>();
  const [fPosition, setFPosition] = useState<string | undefined>();
  const [fGrade, setFGrade] = useState<string | undefined>();
  // 行插入：'before' / 'after' anchor row；'append' 加到末尾
  const [insertContext, setInsertContext] = useState<
    | { mode: 'append' }
    | { mode: 'before'; anchorEmpId: number; anchorName: string }
    | { mode: 'after'; anchorEmpId: number; anchorName: string }
    | null
  >(null);

  // 自訂欄狀態（按 batchId 持久化到 localStorage）
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customCells, setCustomCells] = useState<
    Record<number, Record<string, string>>
  >({});
  const [columnOrder, setColumnOrder] =
    useState<ColumnOrderItem[]>(DEFAULT_COLUMN_ORDER);
  // 「表彰/晋升」每行的选择（多选数组），按 batchId 持久化到 localStorage。
  // 老数据（v1 的单选 string）会在加载时迁移：'praise' → ['praise']，'promote' → ['direct_promote']。
  const [recognitionMap, setRecognitionMap] = useState<
    Record<number, RecognitionType[]>
  >({});
  // 草稿持久化：admin 手填的「本次拟晋升职级 / 涨薪 / 晋升理由」按 batchId 存 localStorage。
  // 关键：rows 每次重新生成（重新点击"重新生成"或权重变化）会被系统建议覆盖；
  //       但用户的手填值不能丢——load data 时按 employeeId merge 回 rows。
  // 初版（2026-05 b0055a6）漏了这层持久化，导致老板填的拟晋升职级 / 涨薪刷新即丢，
  // 由秦艳芳 chat 现场反馈后补上。
  const [draftMap, setDraftMap] = useState<
    Record<
      number,
      {
        proposedLevelText?: string;
        proposedAmount?: number | null;
        proposedReason?: string;
      }
    >
  >({});
  const [insertColModal, setInsertColModal] = useState<{
    refIdx: number;
    side: 'before' | 'after';
    anchorTitle: string;
    title: string;
  } | null>(null);
  const [renameColModal, setRenameColModal] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const queryParams = useMemo(
    () => ({
      batchId: batchId!,
      percent,
      wNinebox: weights.ninebox,
      wNomination: weights.nomination,
      wCompetency: weights.competency,
      wHp: weights.hp,
      _gen: generationKey,
    }),
    [batchId, percent, weights, generationKey],
  );

  const weightsSum = useMemo(
    () =>
      Number(weights.ninebox || 0) +
      Number(weights.nomination || 0) +
      Number(weights.competency || 0) +
      Number(weights.hp || 0),
    [weights],
  );
  const weightsValid = Math.abs(weightsSum - 100) < 0.01;

  const { data, isFetching, refetch } = useQuery<CandidatesResp>({
    queryKey: ['promotion-plan-candidates', queryParams],
    queryFn: () =>
      api.get('/admin/promotion-plan/candidates', { params: queryParams }),
    enabled: !!batchId && weightsValid,
  });

  // 接收新一轮系统建议时，覆盖 rows / pool；同时把 draftMap 中的手填值 merge 回去
  // （以 employeeId 为键），保证 admin 重算后他自己改过的拟晋升职级 / 涨薪 / 理由不丢。
  useEffect(() => {
    if (!data) return;
    setRows(
      data.candidates.map((c) => {
        const d = draftMap[c.employeeId];
        if (!d) return { ...c };
        return {
          ...c,
          proposedLevelText:
            d.proposedLevelText !== undefined
              ? d.proposedLevelText
              : c.proposedLevelText,
          proposedAmount:
            d.proposedAmount !== undefined ? d.proposedAmount : c.proposedAmount,
          proposedReason:
            d.proposedReason !== undefined ? d.proposedReason : c.proposedReason,
        };
      }),
    );
    setPool(data.pool.map((c) => ({ ...c })));
    setMeta({
      totalActive: data.totalActive,
      excludedRedLine: data.excludedRedLine,
      nominatedTotal: data.nominatedTotal ?? 0,
      defaultConfirmYearMonth: data.batch.defaultConfirmYearMonth,
      performancePeriodMonths: data.batch.performancePeriodMonths ?? [],
    });
    setConfirmYearMonth(data.batch.defaultConfirmYearMonth);
    // draftMap 故意不进依赖：data 变化时（"重新生成"/批次切）才 merge；
    // draftMap 自身变化由 updateRow 直接 setRows 同步，无需 effect 二次覆盖。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // 切批次时清除本地内存中的 rows / pool / meta / 筛选条件；
  // confirmedSet / draftMap 不在这里清——由下方 load 副作用按 batchId 从 localStorage 重新加载。
  useEffect(() => {
    setRows([]);
    setPool([]);
    setMeta(null);
    setFPromotion('all');
    setFDept(undefined);
    setFName(undefined);
    setFPosition(undefined);
    setFGrade(undefined);
  }, [batchId]);

  // 切批次时载入该批次的自訂欄设置 + 自訂欄数据
  useEffect(() => {
    if (!batchId) {
      setCustomColumns([]);
      setColumnOrder(DEFAULT_COLUMN_ORDER);
      setCustomCells({});
      return;
    }
    try {
      const colsRaw = localStorage.getItem(
        `tg.promotionPlan.cols.${batchId}`,
      );
      if (colsRaw) {
        const parsed = JSON.parse(colsRaw);
        let loadedCustomCols: CustomColumn[] = Array.isArray(parsed.customColumns)
          ? parsed.customColumns
          : [];
        let loadedOrder: ColumnOrderItem[] = Array.isArray(parsed.columnOrder)
          ? parsed.columnOrder
          : DEFAULT_COLUMN_ORDER;

        // 一次性迁移：把历史用户手动添加的「表彰/晋升」自訂列升级为内置 recognitionType 列。
        // 旧自訂列只是文本框，无法染色；新内置列带下拉 + 整行染色。
        // 策略：找到所有 title='表彰/晋升' 的自訂列，记录它们在 columnOrder 里的最早位置，
        // 然后把这些自訂列从 customColumns / columnOrder 全部移除，并在该位置插入 base 列。
        const legacyIds = new Set(
          loadedCustomCols
            .filter((c) => (c.title ?? '').trim() === '表彰/晋升')
            .map((c) => c.id),
        );
        let legacyAnchorIdx = -1;
        if (legacyIds.size > 0) {
          legacyAnchorIdx = loadedOrder.findIndex(
            (it) => it.kind === 'custom' && legacyIds.has(it.id),
          );
          loadedCustomCols = loadedCustomCols.filter(
            (c) => !legacyIds.has(c.id),
          );
          loadedOrder = loadedOrder.filter(
            (it) => !(it.kind === 'custom' && legacyIds.has(it.id)),
          );
        }

        // 防御：丢弃 columnOrder 中找不到 customColumns 的孤儿；筛掉非法 base key
        const validIds = new Set(loadedCustomCols.map((c) => c.id));
        const filteredOrder = loadedOrder.filter((it) =>
          it.kind === 'base'
            ? (BASE_COLUMN_KEYS as readonly string[]).includes(it.key)
            : validIds.has(it.id),
        );
        const presentBase = new Set(
          filteredOrder
            .filter((it) => it.kind === 'base')
            .map((it) => (it as { kind: 'base'; key: string }).key),
        );
        const missingBase = BASE_COLUMN_KEYS.filter((k) => !presentBase.has(k));

        // 缺失 base 列的回填：每个缺失列优先插到「锚点列」之后；锚点不存在则追加末尾。
        // 这样老用户保存的列顺序在升级后能尽量贴近设计意图，不会把新列全堆到行尾。
        //
        // 锚点选择（key → 锚点 key）：
        //   recognitionType    → 'index'（第二列；旧自訂列存在则覆盖到自訂列原位）
        //   currentLevelSalary → 'jobLevel'（紧跟等级后）
        //   avgPayLast6Months  → 'currentLevelSalary' 已存在则跟它，否则跟 jobLevel
        //   其它（理论上不该缺）→ 末尾
        let repaired: ColumnOrderItem[] = [...filteredOrder];
        const insertAfter = (anchorKey: string, key: string) => {
          const anchorIdx = repaired.findIndex(
            (it) => it.kind === 'base' && it.key === anchorKey,
          );
          const insertAt = anchorIdx >= 0 ? anchorIdx + 1 : repaired.length;
          repaired.splice(insertAt, 0, { kind: 'base', key });
        };
        const handled = new Set<string>();

        if (missingBase.includes('recognitionType')) {
          let insertAt: number;
          if (legacyAnchorIdx >= 0) {
            insertAt = Math.min(legacyAnchorIdx, repaired.length);
          } else {
            const indexAt = repaired.findIndex(
              (it) => it.kind === 'base' && it.key === 'index',
            );
            insertAt = indexAt >= 0 ? indexAt + 1 : 1;
          }
          repaired.splice(insertAt, 0, {
            kind: 'base',
            key: 'recognitionType',
          });
          handled.add('recognitionType');
        }
        if (missingBase.includes('currentLevelSalary')) {
          insertAfter('jobLevel', 'currentLevelSalary');
          handled.add('currentLevelSalary');
        }
        if (missingBase.includes('avgPayLast6Months')) {
          // 优先紧跟刚补好的 currentLevelSalary
          insertAfter(
            repaired.some(
              (it) => it.kind === 'base' && it.key === 'currentLevelSalary',
            )
              ? 'currentLevelSalary'
              : 'jobLevel',
            'avgPayLast6Months',
          );
          handled.add('avgPayLast6Months');
        }
        const otherMissing = missingBase.filter((k) => !handled.has(k));
        repaired = [
          ...repaired,
          ...otherMissing.map(
            (key) => ({ kind: 'base', key } as ColumnOrderItem),
          ),
        ];
        setCustomColumns(loadedCustomCols);
        setColumnOrder(repaired);
      } else {
        setCustomColumns([]);
        setColumnOrder(DEFAULT_COLUMN_ORDER);
      }
      const cellsRaw = localStorage.getItem(
        `tg.promotionPlan.cells.${batchId}`,
      );
      setCustomCells(cellsRaw ? JSON.parse(cellsRaw) : {});

      // 加载 recognitionMap，做两层兼容：
      //   v1（最早的 string 单选）：'praise' → ['praise']；'promote' → ['direct_promote']
      //   v2（中间的 string 单选 + 新选项名）：原样升级为单元素数组
      //   v3（当前 string[] 多选）：保留并过滤掉非法值
      const recRaw = localStorage.getItem(
        `tg.promotionPlan.recognition.${batchId}`,
      );
      try {
        const parsed = recRaw ? JSON.parse(recRaw) : {};
        const cleaned: Record<number, RecognitionType[]> = {};
        for (const k of Object.keys(parsed)) {
          const v = (parsed as Record<string, unknown>)[k];
          let arr: RecognitionType[] = [];
          if (typeof v === 'string') {
            // v1/v2：单字符串 → 单元素数组（含旧 'promote' → 'direct_promote' 的语义升级）
            const upgraded =
              v === 'promote' ? 'direct_promote' : (v as RecognitionType);
            if (RECOGNITION_VALUE_SET.has(upgraded as RecognitionType)) {
              arr = [upgraded as RecognitionType];
            }
          } else if (Array.isArray(v)) {
            const seen = new Set<RecognitionType>();
            for (const x of v) {
              const upgraded =
                x === 'promote' ? 'direct_promote' : (x as RecognitionType);
              if (
                RECOGNITION_VALUE_SET.has(upgraded as RecognitionType) &&
                !seen.has(upgraded as RecognitionType)
              ) {
                seen.add(upgraded as RecognitionType);
                arr.push(upgraded as RecognitionType);
              }
            }
          }
          if (arr.length > 0) cleaned[Number(k)] = arr;
        }
        setRecognitionMap(cleaned);
      } catch {
        setRecognitionMap({});
      }
    } catch {
      setCustomColumns([]);
      setColumnOrder(DEFAULT_COLUMN_ORDER);
      setCustomCells({});
      setRecognitionMap({});
    }
  }, [batchId]);

  // recognitionMap → localStorage
  useEffect(() => {
    if (!batchId) return;
    try {
      localStorage.setItem(
        `tg.promotionPlan.recognition.${batchId}`,
        JSON.stringify(recognitionMap),
      );
    } catch {
      // 忽略
    }
  }, [batchId, recognitionMap]);

  // ---- 草稿（draftMap）持久化：load + save ----
  // 切批次时从 localStorage 加载 draftMap；任何修改实时写回。
  useEffect(() => {
    if (!batchId) {
      setDraftMap({});
      return;
    }
    try {
      const raw = localStorage.getItem(`tg.promotionPlan.draft.${batchId}`);
      setDraftMap(raw ? JSON.parse(raw) : {});
    } catch {
      setDraftMap({});
    }
  }, [batchId]);
  useEffect(() => {
    if (!batchId) return;
    try {
      localStorage.setItem(
        `tg.promotionPlan.draft.${batchId}`,
        JSON.stringify(draftMap),
      );
    } catch {
      // 忽略
    }
  }, [batchId, draftMap]);

  // ---- confirmedSet 持久化：load + save（按 batchId）----
  // 业务：admin 在表格逐行点「确定晋升」/「晋升候选」开关；这个状态过去只在 useState 里
  // 刷新页面 / 切批次再切回来就丢——直接导致 2026-05-10 老板 "我点过的确定晋升怎么消失了" 现场反馈。
  // 现在改为按 batchId localStorage，与 recognitionMap / draftMap 同节奏持久化。
  useEffect(() => {
    if (!batchId) {
      setConfirmedSet(new Set());
      return;
    }
    try {
      const raw = localStorage.getItem(
        `tg.promotionPlan.confirmed.${batchId}`,
      );
      const arr: number[] = raw ? JSON.parse(raw) : [];
      setConfirmedSet(new Set(Array.isArray(arr) ? arr : []));
    } catch {
      setConfirmedSet(new Set());
    }
  }, [batchId]);
  useEffect(() => {
    if (!batchId) return;
    try {
      localStorage.setItem(
        `tg.promotionPlan.confirmed.${batchId}`,
        JSON.stringify(Array.from(confirmedSet)),
      );
    } catch {
      // 忽略
    }
  }, [batchId, confirmedSet]);

  // ---- 服务端草稿持久化（PromotionPlanDraft 表）----
  // 背景：在此之前所有"手填态"（draftMap / confirmedSet / customColumns / customCells /
  //       recognitionMap / columnOrder）只活在 React state + localStorage——只要换电脑、
  //       清浏览器、或刷新前 React 状态没刷出去就全丢，已经多次发生现场掉数据事故。
  // 现在加一层服务器持久化：
  //   1) 切批次时先 GET /admin/promotion-plan/draft，如果后端有数据，全量覆盖本地 state
  //      （后端是 source of truth）；如果后端没有数据 → 保留 localStorage 加载到的本地值，
  //      并立即触发一次 POST 把它"种"到服务器（数据迁移老板手填的 localStorage 历史值）。
  //   2) draftLoaded 之后，任何手填字段变化 debounced 600ms POST 一次（patch upsert）。
  //   3) localStorage 仍然写——作为离线/服务器不可达时的本地兜底。
  const [draftLoaded, setDraftLoaded] = useState(false);
  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 标记本批次草稿是否需要立刻反向同步（即后端为空但本地有值，要把本地值上推到服务端做迁移）
  const draftPushOnLoadRef = useRef(false);
  useEffect(() => {
    setDraftLoaded(false);
    if (!batchId) return;
    let cancelled = false;
    api
      .get<{
        exists: boolean;
        draftMap?: Record<
          number,
          {
            proposedLevelText?: string;
            proposedAmount?: number | null;
            proposedReason?: string;
          }
        > | null;
        confirmedIds?: number[] | null;
        customColumns?: CustomColumn[] | null;
        customCells?: Record<number, Record<string, string>> | null;
        recognitionMap?: Record<number, RecognitionType[]> | null;
        columnOrder?: ColumnOrderItem[] | null;
      }>('/admin/promotion-plan/draft', { params: { batchId } })
      .then((resp: any) => {
        if (cancelled) return;
        if (resp?.exists) {
          // 后端有数据：全量覆盖（后端为 source of truth）
          if (resp.draftMap && typeof resp.draftMap === 'object') {
            setDraftMap(resp.draftMap);
          }
          if (Array.isArray(resp.confirmedIds)) {
            setConfirmedSet(new Set(resp.confirmedIds.map((n: any) => Number(n))));
          }
          if (Array.isArray(resp.customColumns)) {
            setCustomColumns(resp.customColumns);
          }
          if (resp.customCells && typeof resp.customCells === 'object') {
            setCustomCells(resp.customCells);
          }
          if (resp.recognitionMap && typeof resp.recognitionMap === 'object') {
            // 复用 v3 校验：只保留合法值
            const cleaned: Record<number, RecognitionType[]> = {};
            for (const k of Object.keys(resp.recognitionMap)) {
              const v = (resp.recognitionMap as Record<string, unknown>)[k];
              if (Array.isArray(v)) {
                const arr: RecognitionType[] = [];
                const seen = new Set<RecognitionType>();
                for (const x of v) {
                  if (
                    RECOGNITION_VALUE_SET.has(x as RecognitionType) &&
                    !seen.has(x as RecognitionType)
                  ) {
                    seen.add(x as RecognitionType);
                    arr.push(x as RecognitionType);
                  }
                }
                if (arr.length > 0) cleaned[Number(k)] = arr;
              }
            }
            setRecognitionMap(cleaned);
          }
          if (Array.isArray(resp.columnOrder) && resp.columnOrder.length > 0) {
            setColumnOrder(resp.columnOrder);
          }
          draftPushOnLoadRef.current = false;
        } else {
          // 后端为空：保留 localStorage 加载到的本地值，并立刻反向 push 一次
          // （让历史 localStorage 数据在用户第一次进入这个批次时就上传到服务端）
          draftPushOnLoadRef.current = true;
        }
        setDraftLoaded(true);
      })
      .catch((err: any) => {
        if (cancelled) return;
        // 服务器不可达：完全 fallback localStorage（不上推，避免覆盖服务器侧可能存在的版本）
        // eslint-disable-next-line no-console
        console.warn('[PromotionPlan] 加载服务端草稿失败，使用本地缓存：', err);
        draftPushOnLoadRef.current = false;
        setDraftLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [batchId]);

  // debounced 保存：任何手填字段变化 → 600ms 后 POST 一次（patch upsert）
  useEffect(() => {
    if (!batchId || !draftLoaded) return;
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
    }
    // 第一次 load 完成后，如果是"反向迁移"模式，立刻保存（不等 debounce）
    const delay = draftPushOnLoadRef.current ? 0 : 600;
    draftPushOnLoadRef.current = false;
    draftSaveTimerRef.current = setTimeout(() => {
      api
        .post('/admin/promotion-plan/draft', {
          batchId,
          draftMap,
          confirmedIds: Array.from(confirmedSet),
          customColumns,
          customCells,
          recognitionMap,
          columnOrder,
        })
        .catch((err: any) => {
          // eslint-disable-next-line no-console
          console.warn('[PromotionPlan] 保存服务端草稿失败（本地仍已写入）：', err);
        });
    }, delay);
    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
        draftSaveTimerRef.current = null;
      }
    };
  }, [
    batchId,
    draftLoaded,
    draftMap,
    confirmedSet,
    customColumns,
    customCells,
    recognitionMap,
    columnOrder,
  ]);

  // 多选版：传 null/undefined/[] 都表示清空该行；传数组则覆盖。
  // 数组顺序保留用户的勾选顺序——row 染色使用 [0]，所以"先勾哪个 → 用哪个色"。
  const setRecognition = (
    employeeId: number,
    value: RecognitionType[] | null | undefined,
  ) => {
    setRecognitionMap((prev) => {
      const next = { ...prev };
      if (!value || value.length === 0) {
        delete next[employeeId];
      } else {
        const seen = new Set<RecognitionType>();
        const cleaned: RecognitionType[] = [];
        for (const v of value) {
          if (RECOGNITION_VALUE_SET.has(v) && !seen.has(v)) {
            seen.add(v);
            cleaned.push(v);
          }
        }
        if (cleaned.length === 0) delete next[employeeId];
        else next[employeeId] = cleaned;
      }
      return next;
    });
  };

  // 自訂欄 / 顺序 → localStorage
  useEffect(() => {
    if (!batchId) return;
    try {
      localStorage.setItem(
        `tg.promotionPlan.cols.${batchId}`,
        JSON.stringify({ customColumns, columnOrder }),
      );
    } catch {
      // 忽略：localStorage 满或无权限
    }
  }, [batchId, customColumns, columnOrder]);

  // 自訂欄单元格内容 → localStorage
  useEffect(() => {
    if (!batchId) return;
    try {
      localStorage.setItem(
        `tg.promotionPlan.cells.${batchId}`,
        JSON.stringify(customCells),
      );
    } catch {
      // 忽略
    }
  }, [batchId, customCells]);

  const { data: savedData } = useQuery<SavedResp>({
    queryKey: ['promotion-plan-saved', batchId],
    queryFn: () =>
      api.get('/admin/promotion-plan/saved', { params: { batchId } }),
    enabled: !!batchId,
  });

  const totalAmount = useMemo(
    () =>
      rows.reduce(
        (s, r) => s + (typeof r.proposedAmount === 'number' ? r.proposedAmount : 0),
        0,
      ),
    [rows],
  );

  const updateRow = (employeeId: number, patch: Partial<CandidateRow>) => {
    setRows((prev) =>
      prev.map((r) =>
        r.employeeId === employeeId ? { ...r, ...patch } : r,
      ),
    );
    // 把可持久化的 3 个字段同步进 draftMap，写 localStorage —— 防刷新即丢。
    if (
      'proposedLevelText' in patch ||
      'proposedAmount' in patch ||
      'proposedReason' in patch
    ) {
      setDraftMap((prev) => {
        const cur = prev[employeeId] ?? {};
        const next = {
          ...cur,
          ...('proposedLevelText' in patch
            ? { proposedLevelText: patch.proposedLevelText }
            : {}),
          ...('proposedAmount' in patch
            ? { proposedAmount: patch.proposedAmount ?? null }
            : {}),
          ...('proposedReason' in patch
            ? { proposedReason: patch.proposedReason }
            : {}),
        };
        return { ...prev, [employeeId]: next };
      });
    }
  };

  const removeRow = (employeeId: number) => {
    setRows((prev) => prev.filter((r) => r.employeeId !== employeeId));
    setConfirmedSet((prev) => {
      if (!prev.has(employeeId)) return prev;
      const next = new Set(prev);
      next.delete(employeeId);
      return next;
    });
  };

  const toggleConfirmed = (employeeId: number) => {
    setConfirmedSet((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  // ---- 自訂欄操作 ----
  const insertCustomColumn = (
    refIdx: number,
    side: 'before' | 'after',
    title: string,
  ) => {
    const trimmed = title.trim();
    if (!trimmed) {
      message.warning('请输入列名');
      return;
    }
    const id = `cc_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 6)}`;
    setCustomColumns((prev) => [...prev, { id, title: trimmed }]);
    setColumnOrder((prev) => {
      const next = [...prev];
      const insertAt = side === 'before' ? refIdx : refIdx + 1;
      next.splice(Math.max(0, Math.min(insertAt, next.length)), 0, {
        kind: 'custom',
        id,
      });
      return next;
    });
  };

  const removeCustomColumn = (id: string) => {
    setCustomColumns((prev) => prev.filter((c) => c.id !== id));
    setColumnOrder((prev) =>
      prev.filter((it) => !(it.kind === 'custom' && it.id === id)),
    );
    setCustomCells((prev) => {
      const next: Record<number, Record<string, string>> = {};
      for (const k of Object.keys(prev)) {
        const empId = Number(k);
        const inner = { ...prev[empId] };
        delete inner[id];
        next[empId] = inner;
      }
      return next;
    });
  };

  const renameCustomColumn = (id: string, title: string) => {
    const trimmed = title.trim();
    if (!trimmed) {
      message.warning('列名不能为空');
      return;
    }
    setCustomColumns((prev) =>
      prev.map((c) => (c.id === id ? { ...c, title: trimmed } : c)),
    );
  };

  const updateCustomCell = (
    employeeId: number,
    colId: string,
    value: string,
  ) => {
    setCustomCells((prev) => ({
      ...prev,
      [employeeId]: { ...(prev[employeeId] ?? {}), [colId]: value },
    }));
  };

  const openPoolDrawer = (
    ctx:
      | { mode: 'append' }
      | { mode: 'before'; anchorEmpId: number; anchorName: string }
      | { mode: 'after'; anchorEmpId: number; anchorName: string },
  ) => {
    setInsertContext(ctx);
    setPoolDrawerOpen(true);
  };

  const addFromPool = (row: CandidateRow) => {
    if (rows.some((r) => r.employeeId === row.employeeId)) {
      message.info('已在名单中');
      return;
    }
    setRows((prev) => {
      const ctx = insertContext ?? { mode: 'append' as const };
      if (ctx.mode === 'append') {
        return [...prev, { ...row }];
      }
      const idx = prev.findIndex((r) => r.employeeId === ctx.anchorEmpId);
      if (idx < 0) return [...prev, { ...row }];
      const insertIdx = ctx.mode === 'before' ? idx : idx + 1;
      const next = prev.slice();
      next.splice(insertIdx, 0, { ...row });
      return next;
    });
    const where =
      insertContext?.mode === 'before'
        ? `已在「${insertContext.anchorName}」上方插入：${row.name}`
        : insertContext?.mode === 'after'
          ? `已在「${insertContext.anchorName}」下方插入：${row.name}`
          : `已加入：${row.name}`;
    message.success(where);
  };

  const handleRegenerate = () => {
    setGenerationKey((k) => k + 1);
    refetch();
  };

  const handleExport = async () => {
    if (rows.length === 0) {
      message.warning('当前名单为空');
      return;
    }
    if (!batchId) return;
    setExporting(true);
    try {
      // 把屏幕上看到的全部字段（自訂列、表彰多选、晋升状态、列序）一起回传给后端，
      // 让导出 Excel 与界面所见严格一致——避免「秦艳芳备注」这种用户加的列丢失。
      const confirmedIds = Array.from(confirmedSet);
      const resp = await http.post(
        '/admin/promotion-plan/export.xlsx',
        {
          batchId,
          items: rows,
          customColumns,
          customCells,
          recognitionMap,
          columnOrder,
          confirmedIds,
        },
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const currentBatch = batches.find((b) => b.id === batchId);
      const tag = currentBatch ? batchLabel(currentBatch) : `${batchId}`;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `晋升和表彰人员-${tag}-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success(`已导出 ${rows.length} 条`);
    } catch (e: any) {
      message.error(e?.message ?? '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const handleConfirm = async () => {
    if (rows.length === 0) {
      message.warning('当前名单为空');
      return;
    }
    if (!batchId) return;
    const toCommit = rows.filter((r) => confirmedSet.has(r.employeeId));
    if (toCommit.length === 0) {
      message.warning(
        '请先把要入库的行点为「确定晋升」（默认是「晋升候选」，仅候选不会入库）',
      );
      return;
    }
    const missingLevel = toCommit.find(
      (r) => !r.proposedLevelText || !r.proposedLevelText.trim(),
    );
    if (missingLevel) {
      message.error(`「${missingLevel.name}」未填本次拟晋升职级`);
      return;
    }
    Modal.confirm({
      title: '确认入库本批次拟晋升名单？',
      content: (
        <div>
          <p>
            将向历史晋升写入 <b>{toCommit.length}</b> 条已标记「确定晋升」的记录。
          </p>
          <p style={{ color: '#999', marginBottom: 4 }}>
            还有 <b>{rows.length - toCommit.length}</b> 人停留在「晋升候选」状态，不会入库。
          </p>
          <p style={{ color: '#fa8c16', marginBottom: 0 }}>
            下一步可选择写入月份（默认盘点区间最后一月）。
          </p>
        </div>
      ),
      okText: '下一步',
      cancelText: '再想想',
      onOk: () => {
        setPendingCommit(toCommit);
        setConfirmModalOpen(true);
      },
    });
  };

  const executeConfirm = async () => {
    if (!batchId || pendingCommit.length === 0) return;
    setConfirming(true);
    try {
      const resp = await api.post('/admin/promotion-plan/confirm', {
        batchId,
        yearMonth: confirmYearMonth,
        items: pendingCommit.map((r) => ({
          employeeId: r.employeeId,
          proposedLevelText: r.proposedLevelText.trim(),
          proposedAmount: typeof r.proposedAmount === 'number' ? r.proposedAmount : null,
          proposedReason: r.proposedReason ?? '',
        })),
      });
      message.success(
        `已入库 ${(resp as any)?.count ?? pendingCommit.length} 条到「${(resp as any)?.yearMonth ?? confirmYearMonth}」`,
      );
      setConfirmModalOpen(false);
      setPendingCommit([]);
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? e?.message ?? '入库失败');
    } finally {
      setConfirming(false);
    }
  };

  const filteredPool = useMemo(() => {
    const inListIds = new Set(rows.map((r) => r.employeeId));
    const lower = poolSearch.trim().toLowerCase();
    return pool
      .filter((p) => !inListIds.has(p.employeeId))
      .filter((p) => {
        if (!lower) return true;
        return (
          p.name.toLowerCase().includes(lower) ||
          (p.deptPath ?? '').toLowerCase().includes(lower) ||
          (p.position ?? '').toLowerCase().includes(lower) ||
          p.empNo.toLowerCase().includes(lower)
        );
      });
  }, [pool, rows, poolSearch]);

  // 顶部筛选条：晋升 / 部门 / 姓名 / 职位 / 职级（与下方「表彰人员」卡片同风格）。
  //
  // 2026-05-12 增加「不晋升」沉底排序（农俊董事长要求）：
  // 任何 recognitionMap 含 'not_promote' 的行自动排到表底部，保持其它行的相对顺序不变（稳定排序）。
  // 注意：Antd Table 列级 sorter 会覆盖 dataSource 顺序，那是用户主动操作，符合直觉，不冲突。
  const filteredRows = useMemo(() => {
    let matched = rows;
    if (fPromotion !== 'all') {
      matched = matched.filter((r) => {
        const arr = recognitionMap[r.employeeId] ?? [];
        if (fPromotion === 'unset') return arr.length === 0;
        return arr.includes(fPromotion);
      });
    }
    if (fDept) matched = matched.filter((r) => r.deptPath === fDept);
    if (fName) matched = matched.filter((r) => r.name === fName);
    if (fPosition) matched = matched.filter((r) => r.position === fPosition);
    if (fGrade) matched = matched.filter((r) => r.jobGrade === fGrade);
    const isNotPromote = (r: CandidateRow) =>
      (recognitionMap[r.employeeId] ?? []).includes('not_promote');
    const head: CandidateRow[] = [];
    const tail: CandidateRow[] = [];
    for (const r of matched) {
      if (isNotPromote(r)) tail.push(r);
      else head.push(r);
    }
    return [...head, ...tail];
  }, [rows, fPromotion, fDept, fName, fPosition, fGrade, recognitionMap]);

  const hasTableFilter =
    fPromotion !== 'all' || !!fDept || !!fName || !!fPosition || !!fGrade;

  const nameFilterOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.name)))
        .sort((a, b) => a.localeCompare(b, 'zh-CN'))
        .map((v) => ({ label: v, value: v })),
    [rows],
  );
  const positionFilterOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.position).filter(Boolean) as string[]))
        .sort((a, b) => a.localeCompare(b, 'zh-CN'))
        .map((v) => ({ label: v, value: v })),
    [rows],
  );
  const gradeFilterOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.jobGrade).filter(Boolean) as string[]))
        .sort((a, b) => a.localeCompare(b, 'zh-CN'))
        .map((v) => ({ label: v, value: v })),
    [rows],
  );
  const deptFilterOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.deptPath).filter(Boolean) as string[]))
        .sort((a, b) => a.localeCompare(b, 'zh-CN'))
        .map((v) => ({ label: v, value: v })),
    [rows],
  );

  // 部门 / 职级 / 等级列的下拉筛选项（基于当前 rows 的 distinct 值）
  const deptFilters = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.deptPath).filter(Boolean) as string[]))
        .sort()
        .map((v) => ({ text: v, value: v })),
    [rows],
  );
  const gradeFilters = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.jobGrade).filter(Boolean) as string[]))
        .sort()
        .map((v) => ({ text: v, value: v })),
    [rows],
  );

  // ---- 列级筛选 dropdown 统一封装 ----
  // 文本关键字搜索（用于：姓名 / 职位 / 本次拟晋升职级 / 晋升理由）
  const textSearchFilter = (
    predicate: (record: CandidateRow, kw: string) => boolean,
  ) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: {
      setSelectedKeys: (keys: React.Key[]) => void;
      selectedKeys: React.Key[];
      confirm: () => void;
      clearFilters?: () => void;
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder="输入关键字"
          value={(selectedKeys[0] as string) ?? ''}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          allowClear
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
          >
            搜索
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
          >
            重置
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterOutlined style={{ color: filtered ? '#7c5cbf' : '#8c8c8c' }} />
    ),
    onFilter: (value: React.Key | boolean, record: CandidateRow) =>
      predicate(record, String(value).toLowerCase()),
  });

  // 候选项类筛选（用于：部门/职级/历史晋升/晋升状态）。
  // 与默认 antd `filters` 行为一致，但显式指定漏斗 icon 始终可见——
  // 哪怕候选项数组为空（数据未加载/字段全为空），按钮仍出现，避免列表头看着像"没筛选"。
  const categoryFilterIcon = (filtered: boolean) => (
    <FilterOutlined style={{ color: filtered ? '#7c5cbf' : '#8c8c8c' }} />
  );

  // 数字区间筛选（用于：综合分 / 涨薪）。selectedKeys[0] 序列化为 "min|max"
  const numberRangeFilter = (
    getValue: (r: CandidateRow) => number | null | undefined,
  ) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: {
      setSelectedKeys: (keys: React.Key[]) => void;
      selectedKeys: React.Key[];
      confirm: () => void;
      clearFilters?: () => void;
    }) => {
      const raw = (selectedKeys[0] as string) ?? '';
      const [minStr, maxStr] = raw.split('|');
      const min =
        minStr === '' || minStr == null || Number.isNaN(Number(minStr))
          ? null
          : Number(minStr);
      const max =
        maxStr === '' || maxStr == null || Number.isNaN(Number(maxStr))
          ? null
          : Number(maxStr);
      const apply = (nm: number | null, nx: number | null) => {
        if (nm == null && nx == null) setSelectedKeys([]);
        else setSelectedKeys([`${nm ?? ''}|${nx ?? ''}`]);
      };
      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Space direction="vertical" style={{ marginBottom: 8 }}>
            <InputNumber
              placeholder="≥ 最小值"
              value={min}
              onChange={(v) => apply(v == null ? null : Number(v), max)}
              style={{ width: 188 }}
            />
            <InputNumber
              placeholder="≤ 最大值"
              value={max}
              onChange={(v) => apply(min, v == null ? null : Number(v))}
              style={{ width: 188 }}
            />
          </Space>
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
            >
              筛选
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
            >
              重置
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <FilterOutlined style={{ color: filtered ? '#7c5cbf' : '#8c8c8c' }} />
    ),
    onFilter: (value: React.Key | boolean, record: CandidateRow) => {
      const [minStr, maxStr] = String(value).split('|');
      const min = minStr === '' ? null : Number(minStr);
      const max = maxStr === '' ? null : Number(maxStr);
      const v = getValue(record);
      if (v == null || Number.isNaN(v)) return false;
      if (min != null && !Number.isNaN(min) && v < min) return false;
      if (max != null && !Number.isNaN(max) && v > max) return false;
      return true;
    },
  });

  // ---- 表格列定义（基础列）----
  // 把每一列拆到 key→def 的 map，配合 columnOrder 即可在任意位置插入自訂列。
  const baseColumnDefs: Record<string, ColumnsType<CandidateRow>[number]> = {
    index: {
      key: 'index',
      title: '#',
      width: 50,
      align: 'center' as const,
      render: (_v, _r, idx) => idx + 1,
    },
    recognitionType: {
      key: 'recognitionType',
      title: '晋升',
      width: 240,
      align: 'center' as const,
      filters: [
        { text: '直接晋升', value: 'direct_promote' },
        { text: '挑战晋升', value: 'challenge_promote' },
        { text: '不晋升', value: 'not_promote' },
        // 旧值 'praise'（表彰）：仅当历史 localStorage 残留时才可能被筛到；
        // 保留筛选项是为了让老板能找出历史"表彰"行，不影响新数据
        { text: '表彰（旧）', value: 'praise' },
        { text: '未选', value: '__none' },
      ],
      filterIcon: categoryFilterIcon,
      onFilter: (value, record) => {
        const arr = recognitionMap[record.employeeId];
        if (value === '__none') return !arr || arr.length === 0;
        return Array.isArray(arr) && arr.includes(value as RecognitionType);
      },
      render: (_v, r) => {
        const cur = recognitionMap[r.employeeId] ?? [];
        // 当前行如果命中了历史 'praise' 值，把 'praise' 选项动态注入到 options 末尾并 disabled，
        // 这样可以正确显示"表彰"标签（带颜色），但不能再被新选/取消，
        // 满足农俊董事长「原选项不要动」要求。
        const hasLegacyPraise = cur.includes('praise');
        const baseOptions = RECOGNITION_OPTIONS.map((opt) => ({
          value: opt.value,
          label: (
            <span style={{ color: opt.selectColor, fontWeight: 600 }}>
              {opt.label}
            </span>
          ),
          disabled: false,
        }));
        const fullOptions = hasLegacyPraise
          ? [
              ...baseOptions,
              ...RECOGNITION_LEGACY_OPTIONS.map((opt) => ({
                value: opt.value,
                label: (
                  <span style={{ color: opt.selectColor, fontWeight: 600 }}>
                    {opt.label}（旧）
                  </span>
                ),
                disabled: true,
              })),
            ]
          : baseOptions;
        return (
          <Select<RecognitionType[]>
            mode="multiple"
            size="small"
            placeholder="请选择（可多选）"
            value={cur}
            allowClear
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            onChange={(val) => setRecognition(r.employeeId, val)}
            options={fullOptions}
          />
        );
      },
    },
    currentLevelSalary: {
      key: 'currentLevelSalary',
      title: '目前职级薪资',
      dataIndex: 'currentLevelSalary',
      width: 120,
      align: 'right' as const,
      sorter: (a, b) =>
        (a.currentLevelSalary ?? -1) - (b.currentLevelSalary ?? -1),
      ...numberRangeFilter((r) => r.currentLevelSalary ?? null),
      render: (v: number | null) =>
        v == null ? (
          <span style={{ color: '#bbb' }}>—</span>
        ) : (
          <span>{v.toLocaleString('zh-CN')}</span>
        ),
    },
    avgPayLast6Months: {
      key: 'avgPayLast6Months',
      title: '过往6个月到手工资',
      dataIndex: 'avgPayLast6Months',
      width: 150,
      align: 'right' as const,
      sorter: (a, b) =>
        (a.avgPayLast6Months ?? -1) - (b.avgPayLast6Months ?? -1),
      ...numberRangeFilter((r) => r.avgPayLast6Months ?? null),
      render: (v: number | null) =>
        v == null ? (
          <span style={{ color: '#bbb' }}>—</span>
        ) : (
          <span>
            {v.toLocaleString('zh-CN', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        ),
    },
    deptPath: {
      key: 'deptPath',
      title: '部门',
      dataIndex: 'deptPath',
      width: 140,
      ellipsis: true,
      filterSearch: true,
      filters: deptFilters,
      filterIcon: categoryFilterIcon,
      onFilter: (value, record) => record.deptPath === value,
      render: (v: string | null) => v ?? '—',
    },
    name: {
      key: 'name',
      title: '姓名',
      dataIndex: 'name',
      width: 86,
      fixed: 'left' as const,
      ...textSearchFilter(
        (r, kw) =>
          r.name.toLowerCase().includes(kw) ||
          r.empNo.toLowerCase().includes(kw),
      ),
    },
    position: {
      key: 'position',
      title: '职位',
      dataIndex: 'position',
      width: 110,
      ellipsis: true,
      ...textSearchFilter((r, kw) =>
        (r.position ?? '').toLowerCase().includes(kw),
      ),
    },
    jobGrade: {
      key: 'jobGrade',
      title: '职级',
      dataIndex: 'jobGrade',
      width: 70,
      filters: gradeFilters,
      filterSearch: true,
      filterIcon: categoryFilterIcon,
      onFilter: (value, record) => record.jobGrade === value,
      render: (v: string | null) => v ?? '—',
    },
    jobLevel: {
      key: 'jobLevel',
      title: '等级',
      dataIndex: 'jobLevel',
      width: 60,
      align: 'center' as const,
      ...numberRangeFilter((r) => r.jobLevel ?? null),
      render: (v: number | null) => (v == null ? '—' : v),
    },
    composite: {
      key: 'composite',
      title: '综合分',
      width: 88,
      align: 'center' as const,
      dataIndex: ['scores', 'composite'],
      sorter: (a, b) => a.scores.composite - b.scores.composite,
      defaultSortOrder: 'descend' as const,
      ...numberRangeFilter((r) => r.scores.composite),
      render: (_v, r) => (
        <Tooltip
          title={
            <div style={{ lineHeight: 1.7 }}>
              <div>
                九宫格 {r.scores.ninebox.finalCell ?? '—'}：
                {r.scores.ninebox.value}
              </div>
              <div>
                领导提名（
                {r.scores.nomination.nominators.join('、') || '无'}）：
                {r.scores.nomination.value}
              </div>
              <div>
                胜任力{' '}
                {r.scores.competency.weighted != null
                  ? r.scores.competency.weighted.toFixed(2)
                  : '—'}
                /5：{r.scores.competency.value}
              </div>
              <div>
                高潜「{r.scores.hp.label ?? '—'}」：{r.scores.hp.value}
              </div>
            </div>
          }
        >
          <Tag color="purple" style={{ margin: 0 }}>
            {r.scores.composite.toFixed(2)}
          </Tag>
        </Tooltip>
      ),
    },
    historyPromotion: {
      key: 'historyPromotion',
      title: '历史晋升',
      dataIndex: 'historyPromotion',
      width: 180,
      filters: [
        { text: '有', value: 'has' },
        { text: '无', value: 'none' },
      ],
      filterIcon: categoryFilterIcon,
      onFilter: (value, record) => {
        const has = !!(
          record.historyPromotion && record.historyPromotion.trim()
        );
        return value === 'has' ? has : !has;
      },
      render: (v: string) => (
        <div
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: '#666',
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          {v || '无'}
        </div>
      ),
    },
    proposedLevelText: {
      key: 'proposedLevelText',
      title: '本次拟晋升职级',
      dataIndex: 'proposedLevelText',
      width: 110,
      ...textSearchFilter((r, kw) =>
        kw === ''
          ? true
          : (r.proposedLevelText ?? '').toLowerCase().includes(kw),
      ),
      render: (v: string, r) => (
        <Input
          value={v}
          size="small"
          placeholder="如：P6"
          onChange={(e) =>
            updateRow(r.employeeId, { proposedLevelText: e.target.value })
          }
        />
      ),
    },
    proposedAmount: {
      key: 'proposedAmount',
      title: '涨薪（元）',
      dataIndex: 'proposedAmount',
      width: 110,
      ...numberRangeFilter((r) => r.proposedAmount ?? null),
      render: (v: number | null, r) => (
        <InputNumber
          size="small"
          min={0}
          step={100}
          value={v ?? null}
          onChange={(nv) =>
            updateRow(r.employeeId, {
              proposedAmount:
                nv == null || Number.isNaN(Number(nv)) ? null : Number(nv),
            })
          }
          style={{ width: '100%' }}
          placeholder="选填"
        />
      ),
    },
    proposedReason: {
      key: 'proposedReason',
      title: '晋升理由',
      dataIndex: 'proposedReason',
      width: 220,
      ...textSearchFilter((r, kw) =>
        (r.proposedReason ?? '').toLowerCase().includes(kw),
      ),
      render: (v: string, r) => (
        <Input.TextArea
          value={v}
          size="small"
          autoSize={{ minRows: 2, maxRows: 6 }}
          onChange={(e) =>
            updateRow(r.employeeId, { proposedReason: e.target.value })
          }
        />
      ),
    },
    confirmStatus: {
      key: 'confirmStatus',
      title: '晋升状态',
      width: 96,
      align: 'center' as const,
      filters: [
        { text: '确定晋升', value: 'confirmed' },
        { text: '晋升候选', value: 'candidate' },
      ],
      filterIcon: categoryFilterIcon,
      onFilter: (value, record) => {
        const isConf = confirmedSet.has(record.employeeId);
        return value === 'confirmed' ? isConf : !isConf;
      },
      render: (_v, r) => {
        const isConf = confirmedSet.has(r.employeeId);
        return (
          <Button
            size="small"
            type={isConf ? 'primary' : 'default'}
            icon={isConf ? <CheckCircleFilled /> : undefined}
            onClick={() => toggleConfirmed(r.employeeId)}
            style={{
              width: 78,
              padding: '0 4px',
              borderColor: isConf ? undefined : '#d9d9d9',
            }}
          >
            {isConf ? '确定晋升' : '晋升候选'}
          </Button>
        );
      },
    },
    actions: {
      key: 'actions',
      title: '操作',
      width: 100,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_v, r) => (
        <Space size={2}>
          <Tooltip title="在此行上方插入新人">
            <Button
              type="text"
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={pool.length === 0}
              onClick={() =>
                openPoolDrawer({
                  mode: 'before',
                  anchorEmpId: r.employeeId,
                  anchorName: r.name,
                })
              }
            />
          </Tooltip>
          <Tooltip title="在此行下方插入新人">
            <Button
              type="text"
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={pool.length === 0}
              onClick={() =>
                openPoolDrawer({
                  mode: 'after',
                  anchorEmpId: r.employeeId,
                  anchorName: r.name,
                })
              }
            />
          </Tooltip>
          <Popconfirm
            title={`移除「${r.name}」？`}
            onConfirm={() => removeRow(r.employeeId)}
          >
            <Tooltip title="移除此行">
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  };

  // ---- 自訂列定义 ----
  const customColumnDefs: Record<
    string,
    ColumnsType<CandidateRow>[number]
  > = {};
  for (const cc of customColumns) {
    customColumnDefs[cc.id] = {
      key: `custom_${cc.id}`,
      title: cc.title,
      width: 180,
      render: (_v, r) => (
        <Input.TextArea
          value={customCells[r.employeeId]?.[cc.id] ?? ''}
          size="small"
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder="—"
          onChange={(e) =>
            updateCustomCell(r.employeeId, cc.id, e.target.value)
          }
        />
      ),
    };
  }

  // ---- 列头操作菜单（在此列左/右插入；自訂列还支持重命名/删除）----
  // 把菜单做成「列名旁边的小箭头点击触发」+「整个列头右键也行」双入口，
  // 单点小箭头的好处是可发现：用户不用猜要右键。
  const wrapHeaderWithMenu = (
    titleNode: React.ReactNode,
    idx: number,
    item: ColumnOrderItem,
  ): React.ReactNode => {
    const isCustom = item.kind === 'custom';
    const anchorTitle =
      item.kind === 'base'
        ? BASE_COL_LABEL[item.key] ?? '该列'
        : customColumns.find((c) => c.id === item.id)?.title ?? '该列';

    const menuItems = [
      {
        key: 'before',
        label: '在此列左侧插入列',
        icon: <ArrowLeftOutlined />,
      },
      {
        key: 'after',
        label: '在此列右侧插入列',
        icon: <ArrowRightOutlined />,
      },
      ...(isCustom
        ? [
            { type: 'divider' as const },
            {
              key: 'rename',
              label: '重命名此列',
              icon: <EditOutlined />,
            },
            {
              key: 'delete',
              label: '删除此列',
              icon: <DeleteOutlined />,
              danger: true,
            },
          ]
        : []),
    ];

    const handleMenuClick = ({
      key,
      domEvent,
    }: {
      key: string;
      domEvent: React.MouseEvent | React.KeyboardEvent;
    }) => {
      domEvent.stopPropagation();
      if (key === 'before' || key === 'after') {
        setInsertColModal({
          refIdx: idx,
          side: key as 'before' | 'after',
          anchorTitle,
          title: '',
        });
      } else if (key === 'rename' && item.kind === 'custom') {
        const cur = customColumns.find((c) => c.id === item.id);
        if (cur) setRenameColModal({ id: cur.id, title: cur.title });
      } else if (key === 'delete' && item.kind === 'custom') {
        const cur = customColumns.find((c) => c.id === item.id);
        Modal.confirm({
          title: `删除列「${cur?.title ?? ''}」？`,
          content: '该列在所有行的内容将一并移除（无法恢复）。',
          okText: '删除',
          okButtonProps: { danger: true },
          cancelText: '取消',
          onOk: () => removeCustomColumn(item.id),
        });
      }
    };

    // 外层 contextMenu 触发；内层小箭头 click 触发——两套都共用同一份 menu
    return (
      <Dropdown
        trigger={['contextMenu']}
        menu={{ items: menuItems, onClick: handleMenuClick }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          <span style={{ flex: '0 1 auto', minWidth: 0 }}>{titleNode}</span>
          <Dropdown
            trigger={['click']}
            menu={{ items: menuItems, onClick: handleMenuClick }}
          >
            <Tooltip title="点此或右键列头 → 在此列左/右插入新列">
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                className="tg-col-actions"
                aria-label={`列「${anchorTitle}」操作菜单`}
              >
                <PlusOutlined />
              </span>
            </Tooltip>
          </Dropdown>
        </span>
      </Dropdown>
    );
  };

  // ---- 最终列：按 columnOrder 顺序组装 base + custom，标题包右键菜单 ----
  // fixed 列特殊性：antd 要求 left-fixed 列在最左、right-fixed 列在最右连续放置。
  //   - 用户可能在「姓名」左侧插入自訂列 → 破坏 left-fixed 连续性
  //   - 用户可能在「操作」右侧插入自訂列 → 破坏 right-fixed 连续性
  // 处理策略：只有当 name 是 columnOrder 中第一个非 index 的 base 列时才保留 fixed:'left'；
  //          只有当 actions 是 columnOrder 最后一项时才保留 fixed:'right'。
  const nameOrderIdx = columnOrder.findIndex(
    (it) => it.kind === 'base' && it.key === 'name',
  );
  const actionsOrderIdx = columnOrder.findIndex(
    (it) => it.kind === 'base' && it.key === 'actions',
  );
  // name 之前是否只有 index（其它都不算 left-fixed 的连续起点）
  const nameLeftClean =
    nameOrderIdx >= 0 &&
    columnOrder.slice(0, nameOrderIdx).every(
      (it) => it.kind === 'base' && (it.key === 'index' || it.key === 'name'),
    );
  const actionsAtEnd = actionsOrderIdx === columnOrder.length - 1;

  const finalColumns: ColumnsType<CandidateRow> = columnOrder
    .map((item, idx) => {
      const col =
        item.kind === 'base'
          ? baseColumnDefs[item.key]
          : customColumnDefs[item.id];
      if (!col) return null;
      // antd 的 col.title 类型上允许是 (props) => ReactNode，但本页所有 base/custom
      // 列都给的是字符串/ReactNode，安全起见 cast 一下
      const rawTitle = col.title as React.ReactNode;
      let merged: ColumnsType<CandidateRow>[number] = {
        ...col,
        title: wrapHeaderWithMenu(rawTitle, idx, item),
      };
      // 动态去除可能被破坏的 fixed
      if (item.kind === 'base' && item.key === 'name' && !nameLeftClean) {
        const { fixed: _f, ...rest } = merged as { fixed?: unknown };
        merged = rest as ColumnsType<CandidateRow>[number];
      }
      if (item.kind === 'base' && item.key === 'actions' && !actionsAtEnd) {
        const { fixed: _f, ...rest } = merged as { fixed?: unknown };
        merged = rest as ColumnsType<CandidateRow>[number];
      }
      return merged;
    })
    .filter(Boolean) as ColumnsType<CandidateRow>;

  // 动态计算 scroll.x：所有 base 列宽之和。
  // 列宽与下方 baseColumnDefs 中各列 width 字段保持一致，任一调整都需同步：
  //   index 50 + recognitionType 240 + deptPath 140 + name 86 + position 110 +
  //   jobGrade 70 + jobLevel 60 + currentLevelSalary 120 + avgPayLast6Months 150 +
  //   composite 88 + historyPromotion 180 + proposedLevelText 110 +
  //   proposedAmount 110 + proposedReason 220 + confirmStatus 96 + actions 100 = 1930
  const BASE_TOTAL_WIDTH = 1930;
  const tableScrollX = BASE_TOTAL_WIDTH + customColumns.length * 180;

  return (
    <div>
      <div className="tg-page-title">晋升和表彰人员</div>

      <Card style={{ marginBottom: 16 }}>
        {/* 第一行：批次 / 拟晋升比例 */}
        <Row gutter={[16, 12]} align="middle">
          <Col>
            <Space>
              <span style={{ color: '#666' }}>批次</span>
              <Select
                placeholder="选择批次"
                style={{ width: 240 }}
                value={batchId}
                options={batches.map((b) => ({
                  label: batchLabel(b),
                  value: b.id,
                }))}
                onChange={setBatchId}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <span style={{ color: '#666' }}>拟晋升比例</span>
              <InputNumber
                min={0}
                max={100}
                step={1}
                value={percent}
                onChange={(v) => setPercent(Number(v) || 0)}
                addonAfter="%"
                style={{ width: 110 }}
              />
              <Tooltip title="在「晋升提名汇总」已提交提名的人员中，按综合分排序后取前 N%">
                <InfoCircleOutlined style={{ color: '#999' }} />
              </Tooltip>
            </Space>
          </Col>
        </Row>

        {/* 第二行：4 个权重输入 + 末尾「权重合计 100」实时指示 */}
        <Row gutter={[16, 12]} style={{ marginTop: 12 }} align="middle">
          {(
            [
              { key: 'ninebox', label: '九宫格' },
              { key: 'nomination', label: '领导提名' },
              { key: 'competency', label: '岗位胜任力' },
              { key: 'hp', label: '高潜识别' },
            ] as const
          ).map((w) => (
            <Col key={w.key}>
              <Space>
                <span style={{ color: '#888' }}>{w.label}</span>
                <InputNumber
                  min={0}
                  max={100}
                  step={5}
                  value={weights[w.key]}
                  status={weightsValid ? '' : 'error'}
                  onChange={(v) =>
                    setWeights((prev) => ({ ...prev, [w.key]: Number(v) || 0 }))
                  }
                  addonAfter="%"
                  style={{ width: 110 }}
                />
              </Space>
            </Col>
          ))}
          <Col>
            <span
              style={{
                color: weightsValid ? '#666' : '#cf1322',
                fontWeight: weightsValid ? 400 : 600,
              }}
            >
              权重合计{' '}
              <b
                style={{
                  color: weightsValid ? '#52c41a' : '#cf1322',
                  fontSize: 16,
                }}
              >
                {weightsSum}
              </b>
              <span style={{ color: '#bbb', margin: '0 4px' }}>/</span>
              100
            </span>
          </Col>
        </Row>

        {/* 第三行：操作按钮 + 名单元信息 */}
        <Row gutter={[16, 12]} style={{ marginTop: 12 }} align="middle">
          <Col>
            <Space>
              <Tooltip
                title={
                  weightsValid
                    ? ''
                    : `权重合计当前为 ${weightsSum}%，须刚好 100% 才能生成`
                }
              >
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRegenerate}
                  loading={isFetching}
                  disabled={!weightsValid}
                >
                  重新生成
                </Button>
              </Tooltip>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={exporting}
                disabled={rows.length === 0}
              >
                导出 Excel
              </Button>
              <Tooltip
                title={
                  rows.length === 0
                    ? ''
                    : confirmedSet.size === 0
                      ? '当前还没有标记「确定晋升」的行，仅候选不会入库'
                      : ''
                }
              >
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleConfirm}
                  loading={confirming}
                  disabled={rows.length === 0 || confirmedSet.size === 0}
                >
                  确认入库
                  {confirmedSet.size > 0 && ` (${confirmedSet.size})`}
                </Button>
              </Tooltip>
            </Space>
          </Col>
          <Col flex="auto" />
          <Col>
            {meta && (
              <Space size="middle" style={{ color: '#666' }}>
                <span>
                  已提名：<b>{meta.nominatedTotal}</b>
                </span>
                <span>
                  拟晋升比例：<b>{percent}%</b>
                </span>
                <span>
                  在职：<b>{meta.totalActive}</b>
                </span>
                <span>
                  排除红线：<b>{meta.excludedRedLine}</b>
                </span>
                <span>
                  当前名单：<b style={{ color: '#7c5cbf' }}>{rows.length}</b>
                </span>
                <span>
                  累计薪资：<b>{totalAmount.toLocaleString('zh-CN')}</b> 元
                </span>
              </Space>
            )}
          </Col>
        </Row>

        {!weightsValid && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 12 }}
            message={
              <span>
                四个维度的权重合计当前为 <b>{weightsSum}%</b>，须刚好 <b>100%</b>{' '}
                才能生成名单（差额 {(100 - weightsSum).toFixed(0)}%）。
              </span>
            }
          />
        )}

        {savedData && savedData.records.length > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={
              <span>
                本期「{savedData.records[0]?.yearMonth ?? savedData.defaultConfirmYearMonth}」等月份已入库{' '}
                <b>{savedData.records.length}</b>{' '}
                条 PromotionRecord（重复"确认入库"会按员工 id 覆盖）
              </span>
            }
          />
        )}
      </Card>

      <Card
        title={<span style={{ fontWeight: 600 }}>本批次拟晋升名单</span>}
        extra={
          <Space size={12} wrap>
            <Tag color="orange">{rows.length} 人</Tag>
            <Tag color="geekblue">{confirmedSet.size} 已确定晋升</Tag>
            <Tag color="purple">{rows.length - confirmedSet.size} 候选</Tag>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => openPoolDrawer({ mode: 'append' })}
              disabled={pool.length === 0}
            >
              + 添加晋升人员
            </Button>
            <Tooltip title="在表格末尾追加一列（如自订评语、跟进备注），仅保存在本浏览器">
              <Button
                size="small"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() =>
                  setInsertColModal({
                    refIdx: Math.max(0, columnOrder.length - 1),
                    side: 'after',
                    anchorTitle: '末尾',
                    title: '',
                  })
                }
              >
                加列
              </Button>
            </Tooltip>
          </Space>
        }
      >
        <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Space size={6}>
              <span style={{ color: '#666', fontSize: 13 }}>晋升</span>
              <Select<PromotionTableFilter>
                size="small"
                value={fPromotion}
                style={{ width: 120 }}
                onChange={(v) => setFPromotion(v)}
                options={[
                  { label: '全部', value: 'all' },
                  { label: '直接晋升', value: 'direct_promote' },
                  { label: '挑战晋升', value: 'challenge_promote' },
                  { label: '不晋升', value: 'not_promote' },
                  { label: '未选', value: 'unset' },
                ]}
              />
            </Space>
          </Col>
          <Col>
            <Space size={6}>
              <span style={{ color: '#666', fontSize: 13 }}>部门</span>
              <Select<string>
                size="small"
                value={fDept}
                style={{ width: 180 }}
                placeholder="全部"
                allowClear
                showSearch
                onChange={(v) => setFDept(v)}
                options={deptFilterOptions}
              />
            </Space>
          </Col>
          <Col>
            <Space size={6}>
              <span style={{ color: '#666', fontSize: 13 }}>姓名</span>
              <Select<string>
                size="small"
                value={fName}
                style={{ width: 120 }}
                placeholder="全部"
                allowClear
                showSearch
                onChange={(v) => setFName(v)}
                options={nameFilterOptions}
              />
            </Space>
          </Col>
          <Col>
            <Space size={6}>
              <span style={{ color: '#666', fontSize: 13 }}>职位</span>
              <Select<string>
                size="small"
                value={fPosition}
                style={{ width: 140 }}
                placeholder="全部"
                allowClear
                showSearch
                onChange={(v) => setFPosition(v)}
                options={positionFilterOptions}
              />
            </Space>
          </Col>
          <Col>
            <Space size={6}>
              <span style={{ color: '#666', fontSize: 13 }}>职级</span>
              <Select<string>
                size="small"
                value={fGrade}
                style={{ width: 110 }}
                placeholder="全部"
                allowClear
                showSearch
                onChange={(v) => setFGrade(v)}
                options={gradeFilterOptions}
              />
            </Space>
          </Col>
        </Row>
        <Table<CandidateRow>
          rowKey="employeeId"
          dataSource={filteredRows}
          loading={isFetching}
          pagination={false}
          size="small"
          scroll={{ x: tableScrollX }}
          rowClassName={(r) => {
            // 多选时整行染色按"用户先勾的那一项"颜色——所有勾选的 class 都拼上去，
            // 但 CSS 里相同优先级时后写的覆盖前写的；为求直观，只取数组首项的 class。
            // 用 ALL_RECOGNITION_OPTIONS（含 legacy 'praise'），保证历史值也能染色。
            const arr = recognitionMap[r.employeeId];
            if (!arr || arr.length === 0) return '';
            const opt = ALL_RECOGNITION_OPTIONS.find((o) => o.value === arr[0]);
            return opt?.rowClass ?? '';
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  isFetching
                    ? '生成中…'
                    : hasTableFilter
                      ? '没有匹配的行；试试调整筛选条件'
                      : '暂无已提名人员；请先在「晋升提名汇总」完成提名，或调整比例后重新生成'
                }
              />
            ),
          }}
          columns={finalColumns}
        />
      </Card>

      {/* 2026-05-12：在职级晋升人员主表下方追加"表彰人员"只读卡片。
          独立子组件、单独数据流，不与上方主表的任何 state 共享，保证 0 改动现有逻辑。 */}
      <RecognitionSummarySection
        batchId={batchId}
        batchExportTag={recognitionBatchExportTag}
      />

      <Drawer
        title={
          insertContext?.mode === 'before'
            ? `在「${insertContext.anchorName}」上方插入新人`
            : insertContext?.mode === 'after'
              ? `在「${insertContext.anchorName}」下方插入新人`
              : '加到名单末尾'
        }
        width={720}
        open={poolDrawerOpen}
        onClose={() => {
          setPoolDrawerOpen(false);
          setInsertContext(null);
        }}
        destroyOnClose={false}
      >
        <Space style={{ marginBottom: 12, width: '100%' }} direction="vertical">
          <Input.Search
            placeholder="搜索 姓名 / 部门 / 职位 / 工号"
            value={poolSearch}
            onChange={(e) => setPoolSearch(e.target.value)}
            allowClear
          />
          <span style={{ color: '#999' }}>
            候选池显示综合分排序的全部在职员工（已自动排除红线触发），可手动加入名单。
          </span>
        </Space>
        <Table<CandidateRow>
          rowKey="employeeId"
          dataSource={filteredPool}
          pagination={{ pageSize: 30, showSizeChanger: true }}
          size="small"
          columns={[
            {
              title: '#',
              width: 50,
              render: (_v, _r, idx) => idx + 1,
            },
            {
              title: '部门',
              dataIndex: 'deptPath',
              width: 180,
              ellipsis: true,
              render: (v) => v ?? '—',
            },
            { title: '姓名', dataIndex: 'name', width: 90 },
            { title: '职位', dataIndex: 'position', width: 140, ellipsis: true },
            {
              title: '综合分',
              dataIndex: ['scores', 'composite'],
              width: 90,
              render: (_v, r) => r.scores.composite.toFixed(2),
              sorter: (a, b) => a.scores.composite - b.scores.composite,
              defaultSortOrder: 'descend' as const,
            },
            {
              title: '操作',
              width: 80,
              render: (_v, r) => (
                <Button size="small" type="link" onClick={() => addFromPool(r)}>
                  加入名单
                </Button>
              ),
            },
          ]}
        />
      </Drawer>

      {/* 插入自訂列 */}
      <Modal
        title={
          insertColModal
            ? `在「${insertColModal.anchorTitle}」${
                insertColModal.side === 'before' ? '左侧' : '右侧'
              }插入列`
            : '插入列'
        }
        open={!!insertColModal}
        onCancel={() => setInsertColModal(null)}
        onOk={() => {
          if (!insertColModal) return;
          if (!insertColModal.title.trim()) {
            message.warning('请输入列名');
            return;
          }
          insertCustomColumn(
            insertColModal.refIdx,
            insertColModal.side,
            insertColModal.title,
          );
          setInsertColModal(null);
        }}
        okText="插入"
        cancelText="取消"
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="列名" required>
            <Input
              placeholder="如：面谈日期 / 培训情况 / 备注…"
              autoFocus
              value={insertColModal?.title ?? ''}
              maxLength={20}
              onChange={(e) =>
                setInsertColModal((prev) =>
                  prev ? { ...prev, title: e.target.value } : prev,
                )
              }
              onPressEnter={() => {
                if (!insertColModal) return;
                if (!insertColModal.title.trim()) {
                  message.warning('请输入列名');
                  return;
                }
                insertCustomColumn(
                  insertColModal.refIdx,
                  insertColModal.side,
                  insertColModal.title,
                );
                setInsertColModal(null);
              }}
            />
          </Form.Item>
          <div style={{ color: '#999', fontSize: 12 }}>
            自訂列为文本类型，可在每行编辑内容；按当前批次保存到本地，刷新后保留。
          </div>
        </Form>
      </Modal>

      {/* 重命名自訂列 */}
      <Modal
        title="重命名此列"
        open={!!renameColModal}
        onCancel={() => setRenameColModal(null)}
        onOk={() => {
          if (!renameColModal) return;
          if (!renameColModal.title.trim()) {
            message.warning('列名不能为空');
            return;
          }
          renameCustomColumn(renameColModal.id, renameColModal.title);
          setRenameColModal(null);
        }}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="新列名" required>
            <Input
              autoFocus
              value={renameColModal?.title ?? ''}
              maxLength={20}
              onChange={(e) =>
                setRenameColModal((prev) =>
                  prev ? { ...prev, title: e.target.value } : prev,
                )
              }
              onPressEnter={() => {
                if (!renameColModal) return;
                if (!renameColModal.title.trim()) {
                  message.warning('列名不能为空');
                  return;
                }
                renameCustomColumn(renameColModal.id, renameColModal.title);
                setRenameColModal(null);
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="选择写入历史晋升的月份"
        open={confirmModalOpen}
        onCancel={() => {
          setConfirmModalOpen(false);
          setPendingCommit([]);
        }}
        onOk={executeConfirm}
        okText="确认入库"
        cancelText="取消"
        confirmLoading={confirming}
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          将 <b>{pendingCommit.length}</b> 条记录写入员工历史晋升（可在花名册查看与修改）。
        </p>
        <Form layout="vertical">
          <Form.Item
            label="晋升月份"
            extra={
              meta?.performancePeriodMonths?.length
                ? `须在盘点区间内：${meta.performancePeriodMonths[0]} ~ ${meta.performancePeriodMonths[meta.performancePeriodMonths.length - 1]}`
                : undefined
            }
            required
          >
            <DatePicker
              picker="month"
              style={{ width: '100%' }}
              value={confirmYearMonth ? dayjs(confirmYearMonth, 'YYYY-MM') : null}
              onChange={(d) => d && setConfirmYearMonth(d.format('YYYY-MM'))}
              disabledDate={(d) => {
                const ym = d.format('YYYY-MM');
                const allowed = meta?.performancePeriodMonths ?? [];
                return allowed.length > 0 && !allowed.includes(ym);
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
